import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronDown, Download, Copy, Save, History as HistoryIcon, X, Trash2, AlertTriangle, LogOut, Eye, EyeOff } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, doc, setDoc, deleteDoc, onSnapshot, orderBy, query } from 'firebase/firestore';

// ── Firebase config ──
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);

const STORAGE_KEY = 'ao_hist';
const ROLE_STORAGE_KEY = 'ao_role';
const OWNER_PASSWORD = 'Suji!985'; // Change this to your secure password
const STAFF_PIN = '0706'; // Change this to your preferred staff PIN
const WASTAGE_PIN = '5654'; // Change this to your preferred wastage-only PIN
const TOLERANCE = 0.005;

const tabs = [
  { key: 'sales', label: 'Sales' },
  { key: 'zreport', label: 'Z Report' },
  { key: 'balance', label: 'Balance' },
  { key: 'wastage', label: 'Wastage' },
  { key: 'scratch', label: 'Scratch' },
  { key: 'cashcarry', label: 'C&C' },
  { key: 'history', label: 'History' },
];

const salesServiceRows = [
  { suffix: 'shop', icon: '🛒', label: 'Shop Sales' },
  { suffix: 'pp', icon: '📦', label: 'Paypoint' },
  { suffix: 'pz', icon: '💳', label: 'Payzone' },
  { suffix: 'oy', icon: '🚌', label: 'Oyster' },
  { suffix: 'lo', icon: '🎰', label: 'Lottery' },
  { suffix: 'sc', icon: '🎟️', label: 'Scratch Card' },
  { suffix: 'co', icon: '☕', label: 'Costa Coffee' },
];

function cx(...values) {
  return values.filter(Boolean).join(' ');
}

function SurfaceCard({ className = '', children, ...props }) {
  return <div className={cx('rounded-xl border border-stone-200 bg-white shadow-sm', className)} {...props}>{children}</div>;
}

function AppButton({ className = '', variant = 'default', type = 'button', children, ...props }) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50';
  const styles = variant === 'outline'
    ? 'border border-stone-300 bg-white text-stone-800 hover:bg-stone-100'
    : 'bg-[#c9a84c] text-[#183325] hover:bg-[#d5b867]';

  return (
    <button type={type} className={cx(base, styles, className)} {...props}>
      {children}
    </button>
  );
}

function canUseDOM() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function todayValue() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function createEmptyEntry(date = todayValue()) {
  return {
    date,
    till1: 0, till2: 0,
    t1_shop: 0, t1_pp: 0, t1_pz: 0, t1_oy: 0, t1_lo: 0, t1_sc: 0, t1_co: 0,
    t1_cash: 0, t1_card: 0,
    t2_shop: 0, t2_pp: 0, t2_pz: 0, t2_oy: 0, t2_lo: 0, t2_sc: 0, t2_co: 0,
    t2_cash: 0, t2_card: 0,
    z1_pz: 0, z1_pp: 0, z1_lo: 0, z1_sc: 0, z1_oy: 0,
    z2_pz: 0, z2_pp: 0, z2_lo: 0, z2_sc: 0, z2_oy: 0,
    worldpay: 0,
    paymentSave: 0,
    drawPrize: 0,
    scratchStarts: Array(20).fill(0),
    scratchEnds: Array(20).fill(0),
    dailyPayment: [0, 0, 0, 0],
    dailyPaymentDesc: ['', '', '', ''],
    salary: [0, 0, 0, 0],
    salaryDesc: ['', '', '', ''],
    savings: [0, 0, 0, 0, 0],
    savingsDesc: ['Safe (£130)', 'Green tin (£140)', 'Black tin (£50)', 'Roll (£100)', 'Money bag (T1+T2)'],
    cashDrop: [0, 0, 0, 0, 0],
    cashDropDesc: ['', '', '', '', ''],
    wastage: Array(20).fill(0),
    wastageDesc: Array(20).fill(''),
    wastageOriginal: Array(20).fill(0),
    wastageReduced: Array(20).fill(0),
    wastageItems: Array(20).fill(0),
    wastageBin: Array(20).fill(0),
    wastageTakeHome: Array(20).fill(0),
  };
}

function toNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return `£${Number(value || 0).toFixed(2)}`;
}

function formatDiff(value) {
  return `${value >= 0 ? '+' : '-'}£${Math.abs(value).toFixed(2)}`;
}

function escapeCsvCell(value) {
  const text = String(value ?? '');
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function serviceField(till, suffix) {
  return `t${till}_${suffix}`;
}

function calculateServiceSubtotal(entry, till) {
  return salesServiceRows.reduce((sum, row) => sum + Number(entry[serviceField(till, row.suffix)]), 0);
}

function getSalesValidation(entry) {
  const t1Sub = calculateServiceSubtotal(entry, 1);
  const t2Sub = calculateServiceSubtotal(entry, 2);
  const t1Pay = entry.t1_cash + entry.t1_card;
  const t2Pay = entry.t2_cash + entry.t2_card;
  const t1HasData = entry.till1 > 0 || t1Sub > 0 || t1Pay > 0;
  const t2HasData = entry.till2 > 0 || t2Sub > 0 || t2Pay > 0;
  const t1ServicesDiff = t1Sub - entry.till1;
  const t2ServicesDiff = t2Sub - entry.till2;
  const t1PaymentDiff = t1Pay - entry.till1;
  const t2PaymentDiff = t2Pay - entry.till2;
  const t1ServicesMismatch = t1HasData && Math.abs(t1ServicesDiff) > TOLERANCE;
  const t2ServicesMismatch = t2HasData && Math.abs(t2ServicesDiff) > TOLERANCE;
  const t1PaymentMismatch = t1HasData && Math.abs(t1PaymentDiff) > TOLERANCE;
  const t2PaymentMismatch = t2HasData && Math.abs(t2PaymentDiff) > TOLERANCE;

  return {
    t1Sub, t2Sub, t1Pay, t2Pay, t1HasData, t2HasData,
    t1ServicesMismatch, t2ServicesMismatch, t1PaymentMismatch, t2PaymentMismatch,
    t1ServicesDiff, t2ServicesDiff, t1PaymentDiff, t2PaymentDiff,
    t1HasErrors: t1ServicesMismatch || t1PaymentMismatch,
    t2HasErrors: t2ServicesMismatch || t2PaymentMismatch,
  };
}

function calculateSavedEntry(entry) {
  const totalSales = (entry.till1 || 0) + (entry.till2 || 0);
  const totalCash = (entry.t1_cash || 0) + (entry.t2_cash || 0);
  const totalCard = (entry.t1_card || 0) + (entry.t2_card || 0);
  const shopSalesTotal = (entry.t1_shop || 0) + (entry.t2_shop || 0);

  const dailyPaymentTotal = (entry.dailyPayment?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0);
  const salaryTotal       = (entry.salary?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0);
  const savingsTotal      = (entry.savings?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0);
  const cashDropTotal     = (entry.cashDrop?.reduce((a, b) => (a || 0) + (b || 0), 0) ||
                             entry.safeBox?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0); // backward compat

  // Cash Out = Daily Payment + Salary ONLY
  const totalOut = dailyPaymentTotal + salaryTotal;
  // Balance = Cash - CashOut - Savings
  const balance = totalCash - totalOut - savingsTotal;

  // Legacy fields for backward compat
  const billsTotal = dailyPaymentTotal;
  const safeBoxTotal = cashDropTotal;

  return {
    ...entry,
    totalSales, totalCash, totalCard, shopSalesTotal,
    salaryTotal, billsTotal, savingsTotal, totalOut, balance,
    dailyPaymentTotal, cashDropTotal, safeBoxTotal,
    ts: entry.ts ?? Date.now(),
  };
}

function sanitizeHistoryItem(item) {
  if (!item || typeof item !== 'object') return null;
  const source = item;
  const base = createEmptyEntry(typeof source.date === 'string' && source.date ? source.date : todayValue());
  const merged = { ...base, ...source };

  // Ensure all array fields are valid arrays (migrate old format if needed)
  if (!Array.isArray(merged.dailyPayment) || merged.dailyPayment.length < 4)
    merged.dailyPayment = Array.from({ length: 4 }, (_, i) => merged.dailyPayment?.[i] ?? 0);
  if (!Array.isArray(merged.dailyPaymentDesc) || merged.dailyPaymentDesc.length < 4)
    merged.dailyPaymentDesc = Array.from({ length: 4 }, (_, i) => merged.dailyPaymentDesc?.[i] ?? '');
  if (!Array.isArray(merged.salary) || merged.salary.length < 4)
    merged.salary = Array.from({ length: 4 }, (_, i) => merged.salary?.[i] ?? 0);
  if (!Array.isArray(merged.salaryDesc) || merged.salaryDesc.length < 4)
    merged.salaryDesc = Array.from({ length: 4 }, (_, i) => merged.salaryDesc?.[i] ?? '');
  if (!Array.isArray(merged.savings) || merged.savings.length < 5)
    merged.savings = Array.from({ length: 5 }, (_, i) => merged.savings?.[i] ?? 0);
  const defaultSavingsDesc = ['Safe (£130)', 'Green tin (£140)', 'Black tin (£50)', 'Roll (£100)', 'Money bag (T1+T2)'];
  if (!Array.isArray(merged.scratchStarts) || merged.scratchStarts.length < 20)
    merged.scratchStarts = Array.from({ length: 20 }, (_, i) => merged.scratchStarts?.[i] ?? 0);
  if (!Array.isArray(merged.scratchEnds) || merged.scratchEnds.length < 20)
    merged.scratchEnds = Array.from({ length: 20 }, (_, i) => merged.scratchEnds?.[i] ?? 0);
  if (!Array.isArray(merged.savingsDesc) || merged.savingsDesc.length < 5)
    merged.savingsDesc = Array.from({ length: 5 }, (_, i) => merged.savingsDesc?.[i] || defaultSavingsDesc[i] || '');
  if (!Array.isArray(merged.safeBox) || merged.safeBox.length < 5)
    merged.safeBox = Array.from({ length: 5 }, (_, i) => merged.safeBox?.[i] ?? 0);
  if (!Array.isArray(merged.safeBoxDesc) || merged.safeBoxDesc.length < 5)
    merged.safeBoxDesc = Array.from({ length: 5 }, (_, i) => merged.safeBoxDesc?.[i] ?? '');
  // Migrate safeBox → cashDrop if cashDrop missing
  if (!Array.isArray(merged.cashDrop) || merged.cashDrop.length < 5)
    merged.cashDrop = Array.from({ length: 5 }, (_, i) => merged.cashDrop?.[i] ?? merged.safeBox?.[i] ?? 0);
  if (!Array.isArray(merged.cashDropDesc) || merged.cashDropDesc.length < 5)
    merged.cashDropDesc = Array.from({ length: 5 }, (_, i) => merged.cashDropDesc?.[i] ?? merged.safeBoxDesc?.[i] ?? '');
  if (!Array.isArray(merged.wastage) || merged.wastage.length < 20)
    merged.wastage = Array.from({ length: 20 }, (_, i) => merged.wastage?.[i] ?? 0);
  if (!Array.isArray(merged.wastageDesc) || merged.wastageDesc.length < 20)
    merged.wastageDesc = Array.from({ length: 20 }, (_, i) => merged.wastageDesc?.[i] ?? '');
  if (!Array.isArray(merged.wastageOriginal) || merged.wastageOriginal.length < 20)
    merged.wastageOriginal = Array.from({ length: 20 }, (_, i) => merged.wastageOriginal?.[i] ?? 0);
  if (!Array.isArray(merged.wastageReduced) || merged.wastageReduced.length < 20)
    merged.wastageReduced = Array.from({ length: 20 }, (_, i) => merged.wastageReduced?.[i] ?? 0);
  if (!Array.isArray(merged.wastageItems) || merged.wastageItems.length < 20)
    merged.wastageItems = Array.from({ length: 20 }, (_, i) => merged.wastageItems?.[i] ?? 0);
  if (!Array.isArray(merged.wastageBin) || merged.wastageBin.length < 20)
    merged.wastageBin = Array.from({ length: 20 }, (_, i) => merged.wastageBin?.[i] ?? 0);
  if (!Array.isArray(merged.wastageTakeHome) || merged.wastageTakeHome.length < 20)
    merged.wastageTakeHome = Array.from({ length: 20 }, (_, i) => merged.wastageTakeHome?.[i] ?? 0);

  return calculateSavedEntry(merged);
}

function sortHistory(history) {
  return [...history].sort((a, b) => {
    // Sort by DATE descending (newest date first)
    return b.date.localeCompare(a.date);
  });
}

function readHistory() {
  if (!canUseDOM()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return sortHistory(parsed.map(sanitizeHistoryItem).filter((item) => item !== null));
  } catch {
    return [];
  }
}

function writeHistory(history) {
  if (!canUseDOM()) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sortHistory(history)));
}

function buildCsvFilename(date) {
  return `acton_${date}.csv`;
}

function buildCsvString(entry) {
  const e = calculateSavedEntry(entry);
  const rows = [
    ['', 'TILL 1', 'TILL 2', 'TOTAL'],
    ['Date', e.date, '', ''],
    ['Till Total', e.till1, e.till2, e.totalSales],
    ['Shop Sales', e.t1_shop, e.t2_shop, ''],
    ['Paypoint', e.t1_pp, e.t2_pp, ''],
    ['Payzone', e.t1_pz, e.t2_pz, ''],
    ['Oyster', e.t1_oy, e.t2_oy, ''],
    ['Lottery', e.t1_lo, e.t2_lo, ''],
    ['Scratch', e.t1_sc, e.t2_sc, ''],
    ['Costa', e.t1_co, e.t2_co, ''],
    ['Cash', e.t1_cash, e.t2_cash, e.totalCash],
    ['Card', e.t1_card, e.t2_card, ''],
    ['Z Payzone', e.z1_pz, e.z2_pz, ''],
    ['Z Paypoint', e.z1_pp, e.z2_pp, ''],
    ['Z Lottery', e.z1_lo, e.z2_lo, ''],
    ['Z Scratch Win', e.z1_sc, e.z2_sc, ''],
    ['Z Oyster', e.z1_oy, e.z2_oy, ''],
    ['World Pay', '', '', e.worldpay || 0],
    ['Payment Save', '', '', e.paymentSave || 0],
    ['Drawbased Prize', '', '', e.drawPrize || 0],
    ['Daily Payment 1', '', '', e.dailyPayment?.[0] || 0],
    ['Daily Payment 2', '', '', e.dailyPayment?.[1] || 0],
    ['Daily Payment 3', '', '', e.dailyPayment?.[2] || 0],
    ['Daily Payment 4', '', '', e.dailyPayment?.[3] || 0],
    ['Daily Payment Total', '', '', e.dailyPaymentTotal],
    ['Salary 1', '', '', e.salary?.[0] || 0],
    ['Salary 2', '', '', e.salary?.[1] || 0],
    ['Salary 3', '', '', e.salary?.[2] || 0],
    ['Salary 4', '', '', e.salary?.[3] || 0],
    ['Salary Total', '', '', e.salaryTotal],
    ['Savings 1', '', '', e.savings?.[0] || 0],
    ['Savings 2', '', '', e.savings?.[1] || 0],
    ['Savings 3', '', '', e.savings?.[2] || 0],
    ['Savings 4', '', '', e.savings?.[3] || 0],
    ['Savings 5', '', '', e.savings?.[4] || 0],
    ['Savings Total', '', '', e.savingsTotal],
    ['Safe Box 1', '', '', e.safeBox?.[0] || 0],
    ['Safe Box 2', '', '', e.safeBox?.[1] || 0],
    ['Safe Box 3', '', '', e.safeBox?.[2] || 0],
    ['Safe Box 4', '', '', e.safeBox?.[3] || 0],
    ['Safe Box 5', '', '', e.safeBox?.[4] || 0],
    ['Safe Box Total', '', '', e.safeBoxTotal],
    ['Total Out', '', '', e.totalOut],
    ['Balance Cash', '', '', e.balance],
  ];
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
}

function buildClipboardReport(entry) {
  const e = calculateSavedEntry(entry);
  return `ACTON ORIENTAL – ${e.date}
════════════════════════════════
TOTAL SALES:  ${formatCurrency(e.totalSales)}
TOTAL CASH:   ${formatCurrency(e.totalCash)}
TOTAL OUT:    ${formatCurrency(e.totalOut)}
BALANCE CASH: ${formatCurrency(e.balance)}`;
}

async function copyTextToClipboard(text) {
  if (!canUseDOM()) return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  try {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      document.body.removeChild(textarea);
      return true;
    }
  } catch {}

  try {
    const successful = typeof document.execCommand === 'function' ? document.execCommand('copy') : false;
    document.body.removeChild(textarea);
    return successful;
  } catch {
    document.body.removeChild(textarea);
    return false;
  }
}

function useLocalDataStore() {
  const [entry, setEntry] = useState(() => createEmptyEntry());
  const [history, setHistory] = useState([]);
  const [syncing, setSyncing] = useState(false);

  // ── Real-time listener: any device that saves → all devices update instantly ──
  useEffect(() => {
    const q = query(collection(db, 'ao_entries'), orderBy('ts', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => sanitizeHistoryItem(d.data())).filter(Boolean);
      setHistory(sortHistory(items));
    }, () => {
      // Firestore offline — fall back to localStorage
      setHistory(readHistory());
    });
    return () => unsub();
  }, []);

  const setDate = useCallback((date) => {
    setEntry((current) => ({ ...current, date }));
  }, []);

  const refreshHistory = useCallback(() => {
    return history;
  }, [history]);

  const saveEntry = useCallback(async (role) => {
    if (!entry.date.trim()) return { ok: false };
    const saved = calculateSavedEntry({ 
      ...entry, 
      ts: Date.now(),
      lastEditedBy: role || 'unknown',
      lastEditedAt: new Date().toLocaleString('en-GB', { 
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }),
    });
    setSyncing(true);
    try {
      // Save to Firebase (date is the document ID so it overwrites same day)
      await setDoc(doc(db, 'ao_entries', saved.date), saved);
      // Also save locally as backup
      const current = readHistory();
      const index = current.findIndex((item) => item.date === saved.date);
      const next = [...current];
      if (index >= 0) { next[index] = saved; } else { next.unshift(saved); }
      writeHistory(sortHistory(next));
    } catch {
      // Firebase failed — save locally only
      const current = readHistory();
      const index = current.findIndex((item) => item.date === saved.date);
      const next = [...current];
      if (index >= 0) { next[index] = saved; } else { next.unshift(saved); }
      const sorted = sortHistory(next);
      writeHistory(sorted);
      setHistory(sorted);
    } finally {
      setSyncing(false);
    }
    return { ok: true };
  }, [entry]);

  const loadEntry = useCallback((date) => {
    const item = history.find((record) => record.date === date) ?? null;
    if (item) {
      const safe = { ...item };
      if (!Array.isArray(safe.dailyPayment) || safe.dailyPayment.length < 4)
        safe.dailyPayment = Array.from({ length: 4 }, (_, i) => safe.dailyPayment?.[i] ?? 0);
      if (!Array.isArray(safe.dailyPaymentDesc) || safe.dailyPaymentDesc.length < 4)
        safe.dailyPaymentDesc = Array.from({ length: 4 }, (_, i) => safe.dailyPaymentDesc?.[i] ?? '');
      if (!Array.isArray(safe.salary) || safe.salary.length < 4)
        safe.salary = Array.from({ length: 4 }, (_, i) => safe.salary?.[i] ?? 0);
      if (!Array.isArray(safe.salaryDesc) || safe.salaryDesc.length < 4)
        safe.salaryDesc = Array.from({ length: 4 }, (_, i) => safe.salaryDesc?.[i] ?? '');
      if (!Array.isArray(safe.savings) || safe.savings.length < 5)
        safe.savings = Array.from({ length: 5 }, (_, i) => safe.savings?.[i] ?? 0);
      if (!Array.isArray(safe.savingsDesc) || safe.savingsDesc.length < 5)
        safe.savingsDesc = Array.from({ length: 5 }, (_, i) => safe.savingsDesc?.[i] ?? '');
      if (!Array.isArray(safe.safeBox) || safe.safeBox.length < 5)
        safe.safeBox = Array.from({ length: 5 }, (_, i) => safe.safeBox?.[i] ?? 0);
      if (!Array.isArray(safe.safeBoxDesc) || safe.safeBoxDesc.length < 5)
        safe.safeBoxDesc = Array.from({ length: 5 }, (_, i) => safe.safeBoxDesc?.[i] ?? '');
      setEntry(safe);
      return safe;
    }
    return item;
  }, [history]);

  const deleteEntry = useCallback(async (date) => {
    try {
      await deleteDoc(doc(db, 'ao_entries', date));
    } catch {}
    const next = sortHistory(readHistory().filter((record) => record.date !== date));
    writeHistory(next);
    setHistory(next);
    return next;
  }, []);

  const hasSavedEntryForDate = useCallback((date) => history.some((item) => item.date === date), [history]);

  return { entry, setDate, setEntry, history, refreshHistory, saveEntry, loadEntry, deleteEntry, hasSavedEntryForDate, syncing };
}

function SectionHeader({ title }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <h2 className="text-[10px] font-bold uppercase tracking-widest text-[#c9a84c]">{title}</h2>
      <div className="h-px flex-1 bg-stone-300/70" />
    </div>
  );
}

function NumberInput({ value, onChange, hasError = false, disabled = false }) {
  const fmt = (v) => (v === 0 || v === '' || v === null || v === undefined) ? '' : Number(v).toFixed(2);
  const [localVal, setLocalVal] = useState(() => fmt(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setLocalVal(fmt(value));
  }, [value, focused]);

  return (
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-stone-400 pointer-events-none">£</span>
      <input
        type="number"
        value={localVal}
        onChange={(e) => {
          setLocalVal(e.target.value);
          onChange(e.target.value);
        }}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          const num = parseFloat(e.target.value);
          if (!isNaN(num)) {
            setLocalVal(num.toFixed(2));
            onChange(num.toFixed(2));
          } else {
            setLocalVal('');
            onChange(0);
          }
        }}
        step="0.01"
        placeholder="0.00"
        disabled={disabled}
        className={cx(
          'w-28 rounded-lg border pl-6 pr-2 py-1.5 text-right text-sm text-stone-900',
          disabled ? 'bg-stone-100 cursor-not-allowed opacity-60' : '',
          hasError ? 'border-red-400 bg-red-50 text-red-600' : 'border-stone-300 bg-white',
        )}
      />
    </div>
  );
}

function MismatchBanner({ message, diff }) {
  return (
    <div className="mb-3 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-red-700">
      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      <div className="text-xs font-medium">{message} ({formatDiff(diff)})</div>
    </div>
  );
}

function SalesTab({ entry, setEntry, isStaff }) {
  const [activeTill, setActiveTill] = useState(1);
  const validation = useMemo(() => getSalesValidation(entry), [entry]);
  const till = activeTill;

  const setNumField = useCallback(
    (field, value) => {
      setEntry((current) => ({ ...current, [field]: toNumber(value) }));
    },
    [setEntry],
  );

  const tillConfig = till === 1
    ? {
        subtotal: validation.t1Sub, payTotal: validation.t1Pay,
        servicesMismatch: validation.t1ServicesMismatch, paymentMismatch: validation.t1PaymentMismatch,
        servicesDiff: validation.t1ServicesDiff, paymentDiff: validation.t1PaymentDiff,
      }
    : {
        subtotal: validation.t2Sub, payTotal: validation.t2Pay,
        servicesMismatch: validation.t2ServicesMismatch, paymentMismatch: validation.t2PaymentMismatch,
        servicesDiff: validation.t2ServicesDiff, paymentDiff: validation.t2PaymentDiff,
      };

  return (
    <div className="space-y-3">
      <div>
        <SectionHeader title="Till Totals" />
        <SurfaceCard className="overflow-hidden">
          {[
            ['till1', '🖥️', 'Till 1 Turnover', 'Main counter', validation.t1HasErrors],
            ['till2', '🖥️', 'Till 2 Turnover', 'Secondary', validation.t2HasErrors],
          ].map(([field, icon, label, sub, hasError]) => (
            <div key={field} className={cx('flex items-center gap-3 border-b border-stone-200 px-3 py-2 last:border-b-0', hasError && 'bg-red-50/60')}>
              <div className="flex h-6 w-6 items-center justify-center rounded-md bg-stone-100 text-xs">{icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-stone-900">{label}</div>
                <div className="text-[10px] text-stone-500">{sub}</div>
              </div>
              <NumberInput value={entry[field]} onChange={(v) => setNumField(field, v)} hasError={hasError} disabled={false} />
            </div>
          ))}
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-stone-100 text-xs">💰</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-stone-900">Total Sales</div>
              <div className="text-[10px] text-stone-500">Auto</div>
            </div>
            <div className="rounded-lg bg-stone-100 px-3 py-1.5 text-sm font-bold text-stone-900">{formatCurrency(entry.till1 + entry.till2)}</div>
          </div>
        </SurfaceCard>
      </div>

      <div>
        <SectionHeader title="Breakdown by Till" />
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => setActiveTill(1)}
            className={cx(
              'relative flex-1 rounded-lg border-2 px-2 py-1.5 text-center',
              validation.t1HasErrors ? 'border-red-300 bg-red-50/40 text-red-600' : activeTill === 1 ? 'border-emerald-400 bg-emerald-50 text-emerald-600' : 'border-stone-300 bg-white text-stone-500',
            )}
          >
            {validation.t1HasErrors && <AlertTriangle className="absolute right-2 top-2 h-4 w-4" />}
            <div className="text-sm font-bold">TILL 1</div>
            <div className="mt-1 text-xs">{formatCurrency(validation.t1Sub)}</div>
          </button>
          <button
            type="button"
            onClick={() => setActiveTill(2)}
            className={cx(
              'relative flex-1 rounded-lg border-2 px-2 py-1.5 text-center',
              validation.t2HasErrors ? 'border-red-300 bg-red-50/40 text-red-600' : activeTill === 2 ? 'border-sky-400 bg-sky-50 text-sky-600' : 'border-stone-300 bg-white text-stone-500',
            )}
          >
            {validation.t2HasErrors && <AlertTriangle className="absolute right-2 top-2 h-4 w-4" />}
            <div className="text-sm font-bold">TILL 2</div>
            <div className="mt-1 text-xs">{formatCurrency(validation.t2Sub)}</div>
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <SectionHeader title={`Till ${till} – Services`} />
            {tillConfig.servicesMismatch && <MismatchBanner message={`Services subtotal ≠ Till ${till} total`} diff={tillConfig.servicesDiff} />}
            <SurfaceCard className={cx('overflow-hidden', tillConfig.servicesMismatch && 'border-red-300')}>
              {salesServiceRows.map((row) => {
                const field = serviceField(till, row.suffix);
                return (
                  <div key={field} className="flex items-center gap-3 border-b border-stone-200 px-3 py-2 last:border-b-0">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-stone-100 text-xs">{row.icon}</div>
                    <div className="flex-1 text-sm font-medium text-stone-900">{row.label}</div>
                    <NumberInput value={entry[field] || 0} onChange={(v) => setNumField(field, v)} disabled={false} />
                  </div>
                );
              })}
              <div className={cx('flex items-center gap-2 px-3 py-2', tillConfig.servicesMismatch && 'bg-red-50/60')}>
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-stone-100 text-xs">✅</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900">Subtotal</div>
                  <div className="text-[10px] text-stone-500">Auto</div>
                </div>
                <div className={cx('rounded-lg px-3 py-1.5 text-sm font-bold', tillConfig.servicesMismatch ? 'bg-red-100 text-red-600' : 'bg-stone-100 text-stone-900')}>
                  {formatCurrency(tillConfig.subtotal)}
                </div>
              </div>
            </SurfaceCard>
          </div>

          <div>
            <SectionHeader title={`Till ${till} – Payment`} />
            {tillConfig.paymentMismatch && <MismatchBanner message={`Cash + Card ≠ Till ${till} total`} diff={tillConfig.paymentDiff} />}
            <SurfaceCard className={cx('overflow-hidden', tillConfig.paymentMismatch && 'border-red-300')}>
              {[
                [`t${till}_cash`, '💵', 'Cash'],
                [`t${till}_card`, '💳', 'Card'],
              ].map(([field, icon, label]) => (
                <div key={field} className="flex items-center gap-3 border-b border-stone-200 px-3 py-2 last:border-b-0">
                  <div className="flex h-6 w-6 items-center justify-center rounded-md bg-stone-100 text-xs">{icon}</div>
                  <div className="flex-1 text-sm font-medium text-stone-900">{label}</div>
                  <NumberInput value={entry[field] || 0} onChange={(v) => setNumField(field, v)} disabled={false} />
                </div>
              ))}
              <div className={cx('flex items-center gap-2 px-3 py-2', tillConfig.paymentMismatch && 'bg-red-50/60')}>
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-stone-100 text-xs">✅</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900">Total</div>
                  <div className="text-[10px] text-stone-500">Auto</div>
                </div>
                <div className={cx('rounded-lg px-3 py-1.5 text-sm font-bold', tillConfig.paymentMismatch ? 'bg-red-100 text-red-600' : 'bg-stone-100 text-stone-900')}>
                  {formatCurrency(tillConfig.payTotal)}
                </div>
              </div>
            </SurfaceCard>
          </div>
        </div>
      </div>
    </div>
  );
}

function ZReportTab({ entry, setEntry, isStaff }) {
  const setNumField = useCallback(
    (field, value) => setEntry((current) => ({ ...current, [field]: toNumber(value) })),
    [setEntry],
  );

  // Combined sales from BOTH tills for each service
  const combined = {
    pp: (entry.t1_pp || 0) + (entry.t2_pp || 0),
    pz: (entry.t1_pz || 0) + (entry.t2_pz || 0),
    lo: (entry.t1_lo || 0) + (entry.t2_lo || 0),
    sc: (entry.t1_sc || 0) + (entry.t2_sc || 0),
    oy: (entry.t1_oy || 0) + (entry.t2_oy || 0),
  };

  // Single Z report values (use z1 fields as the single report)
  const zReport = {
    pp: entry.z1_pp || 0,
    pz: entry.z1_pz || 0,
    lo: entry.z1_lo || 0,
    sc: entry.z1_sc || 0,
    oy: entry.z1_oy || 0,
  };

  const zTotal = Object.values(zReport).reduce((a, b) => a + b, 0);
  const salesTotal = Object.values(combined).reduce((a, b) => a + b, 0);

  const rows = [
    { label: 'Payzone',           icon: '💳', key: 'pz', zField: 'z1_pz' },
    { label: 'Paypoint',          icon: '📦', key: 'pp', zField: 'z1_pp' },
    { label: 'Lotto sales',       icon: '🎰', key: 'lo', zField: 'z1_lo' },
    { label: 'Scratchcard prizes',icon: '🎟️', key: 'sc', zField: 'z1_sc', sub: 'Paid out' },
    { label: 'Drawbased prize',   icon: '🎲', key: 'dp', zField: 'drawPrize' },
    { label: 'Oyster',            icon: '🚌', key: 'oy', zField: 'z1_oy' },
    { label: 'World Pay',         icon: '🌐', key: 'wp', zField: 'worldpay' },
    { label: 'Payment Save',      icon: '💾', key: 'ps', zField: 'paymentSave' },
  ];

  const mismatches = rows
    .filter(r => r.key !== 'wp' && r.key !== 'ps' && r.key !== 'dp') // only check service rows
    .filter(r => Math.abs((combined[r.key] || 0) - (zReport[r.key] || 0)) > 0.005);
  const allMatch = mismatches.length === 0 && zTotal > 0;

  return (
    <div className="space-y-3">

      {/* Owner mismatch banner */}
      {!isStaff && mismatches.length > 0 && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-3">
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
            <span className="text-sm font-bold text-red-700">Sales vs Z Report Mismatch</span>
          </div>
          {mismatches.map(r => {
            const diff = (combined[r.key] || 0) - (zReport[r.key] || 0);
            return (
              <div key={r.key} className="mb-1 last:mb-0 text-xs text-red-600">
                <span className="font-semibold">{r.label}:</span> Sales T1+T2 = {formatCurrency(combined[r.key])} | Z Report = {formatCurrency(zReport[r.key])} |
                <span className="font-bold"> {diff > 0 ? `▲ OVER +${formatCurrency(diff)}` : `▼ SHORT ${formatCurrency(Math.abs(diff))}`}</span>
              </div>
            );
          })}
        </div>
      )}

      {!isStaff && allMatch && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 flex items-center gap-2">
          <span className="text-emerald-600 font-bold text-sm">✅ Sales matches Z Report</span>
        </div>
      )}

      <div>
        <SectionHeader title="Z Report" />
        <SurfaceCard className="overflow-hidden">
          {rows.map((row) => {
            const diff = (combined[row.key] || 0) - (zReport[row.key] || 0);
            const hasDiff = Math.abs(diff) > 0.005;
            return (
              <div key={row.key} className="flex items-center gap-3 border-b border-stone-200 px-3 py-2 last:border-b-0">
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-stone-100 text-xs">{row.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900">{row.label}</div>
                  {row.sub && <div className="text-[10px] text-stone-500">{row.sub}</div>}
                  {/* Sales total from both tills */}
                  <div className="text-[10px] text-stone-400">T1+T2 Sales: {formatCurrency(combined[row.key])}</div>
                  {!isStaff && hasDiff && (
                    <div className={cx('text-[10px] font-bold', diff > 0 ? 'text-orange-500' : 'text-red-500')}>
                      {diff > 0 ? `▲ OVER +${formatCurrency(diff)}` : `▼ SHORT ${formatCurrency(Math.abs(diff))}`}
                    </div>
                  )}
                </div>
                <NumberInput value={entry[row.zField] || 0} onChange={(v) => setNumField(row.zField, v)} disabled={false} />
              </div>
            );
          })}
        </SurfaceCard>
      </div>

      <div>
        <SectionHeader title="Z Total" />
        <div className="grid grid-cols-2 gap-2">
          <SurfaceCard className="p-3 text-center">
            <div className="text-[10px] uppercase text-stone-500">Sales T1+T2</div>
            <div className="mt-1 text-base font-bold text-emerald-600">{formatCurrency(salesTotal)}</div>
          </SurfaceCard>
          <SurfaceCard className="p-3 text-center">
            <div className="text-[10px] uppercase text-stone-500">Z Report Total</div>
            <div className={cx('mt-1 text-base font-bold', Math.abs(salesTotal - zTotal) > 0.005 ? 'text-red-500' : 'text-[#c9a84c]')}>{formatCurrency(zTotal)}</div>
          </SurfaceCard>
          <SurfaceCard className="p-3 text-center">
            <div className="text-[10px] uppercase text-stone-500">World Pay</div>
            <div className="mt-1 text-base font-bold text-blue-600">{formatCurrency(entry.worldpay || 0)}</div>
          </SurfaceCard>
          <SurfaceCard className="p-3 text-center">
            <div className="text-[10px] uppercase text-stone-500">Payment Save</div>
            <div className="mt-1 text-base font-bold text-violet-600">{formatCurrency(entry.paymentSave || 0)}</div>
          </SurfaceCard>
          <SurfaceCard className="p-3 text-center">
            <div className="text-[10px] uppercase text-stone-500">Drawbased Prize</div>
            <div className="mt-1 text-base font-bold text-rose-600">{formatCurrency(entry.drawPrize || 0)}</div>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}

function BalanceTab({ entry, setEntry, isStaff }) {
  const setArrField = useCallback((key, index, value) => {
    setEntry((current) => {
      const arr = [...(current[key] || [])];
      arr[index] = toNumber(value);
      return { ...current, [key]: arr };
    });
  }, [setEntry]);

  const dailyPaymentTotal = entry.dailyPayment?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
  const salaryTotal       = entry.salary?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
  const savingsTotal      = entry.savings?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
  const cashDropTotal     = entry.cashDrop?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;

  // Cash Out = Daily Payment + Salary ONLY
  const cashOut = dailyPaymentTotal + salaryTotal;
  const totalCash = (entry.t1_cash || 0) + (entry.t2_cash || 0);
  const totalCard = (entry.t1_card || 0) + (entry.t2_card || 0);
  // Balance = Cash In - Cash Out - Savings
  const balance = totalCash - cashOut - savingsTotal;
  const balanceColor = balance > 0 ? 'text-emerald-500' : balance < 0 ? 'text-red-500' : 'text-stone-400';
  const balancePrefix = balance > 0 ? '+' : '';
  const balanceIcon   = balance > 0 ? '✅' : balance < 0 ? '🔴' : '✓';

  const SAVINGS_LABELS = ['Safe (£130)', 'Green tin (£140)', 'Black tin (£50)', 'Roll (£100)', 'Money bag (T1+T2)'];

  const renderSection = (label, arrayKey, count) => {
    const descKey = arrayKey + 'Desc';
    const total = entry[arrayKey]?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
    return (
      <div>
        <SectionHeader title={label} />
        <SurfaceCard className="overflow-hidden">
          {Array.from({ length: count }, (_, i) => (
            <div key={i} className="flex items-center gap-2 border-b border-stone-200 px-3 py-2 last:border-b-0">
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-stone-300 text-[9px] font-bold text-stone-600">
                {i + 1}
              </div>
              <input
                type="text"
                value={entry[descKey]?.[i] || ''}
                onChange={(e) => {
                  const newDesc = [...(entry[descKey] || Array(count).fill(''))];
                  newDesc[i] = e.target.value;
                  setEntry((cur) => ({ ...cur, [descKey]: newDesc }));
                }}
                placeholder="Description..."
                className="flex-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-[#c9a84c] focus:ring-2 focus:ring-[#c9a84c]/20"
              />
              <div className="relative shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm font-semibold text-stone-500">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={entry[arrayKey]?.[i] || ''}
                  onChange={(e) => setArrField(arrayKey, i, e.target.value)}
                  placeholder="0.00"
                  className="w-24 rounded-lg border border-stone-300 bg-white pl-6 pr-2 py-2 text-sm text-stone-900 outline-none focus:border-[#c9a84c] focus:ring-2 focus:ring-[#c9a84c]/20"
                />
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 px-3 py-2 bg-stone-50">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-stone-100 text-xs font-bold text-stone-600">Σ</div>
            <div className="flex-1 text-sm font-semibold text-stone-700">Total</div>
            <div className="rounded-lg bg-stone-100 px-3 py-1.5 text-sm font-bold text-stone-900">
              £{total.toFixed(2)}
            </div>
          </div>
        </SurfaceCard>
      </div>
    );
  };

  const renderSavingsSection = () => {
    const total = entry.savings?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
    return (
      <div>
        <SectionHeader title="Savings" />
        <SurfaceCard className="overflow-hidden">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex items-center gap-2 border-b border-stone-200 px-3 py-2 last:border-b-0">
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-stone-300 text-[9px] font-bold text-stone-600">
                {i + 1}
              </div>
              {/* Fixed label - cannot be edited */}
              <div className="flex-1 rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 text-sm font-medium text-stone-700">
                {SAVINGS_LABELS[i]}
              </div>
              <div className="relative shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm font-semibold text-stone-500">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={entry.savings?.[i] || ''}
                  onChange={(e) => setArrField('savings', i, e.target.value)}
                  placeholder="0.00"
                  className="w-24 rounded-lg border border-stone-300 bg-white pl-6 pr-2 py-2 text-sm text-stone-900 outline-none focus:border-[#c9a84c] focus:ring-2 focus:ring-[#c9a84c]/20"
                />
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 px-3 py-2 bg-stone-50">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-stone-100 text-xs font-bold text-stone-600">Σ</div>
            <div className="flex-1 text-sm font-semibold text-stone-700">Total</div>
            <div className="rounded-lg bg-stone-100 px-3 py-1.5 text-sm font-bold text-stone-900">
              £{total.toFixed(2)}
            </div>
          </div>
        </SurfaceCard>
      </div>
    );
  };


  return (
    <div className="space-y-3">
      {renderSection('Daily Payment', 'dailyPayment', 4)}
      {renderSection('Salary', 'salary', 4)}
      {renderSavingsSection()}
      {renderSection('Cash Drop', 'cashDrop', 5)}

      <div>
        <SectionHeader title="Balance Cash" />
        <div className="space-y-2">
          <SurfaceCard className="overflow-hidden">
            {[
              { label: '💵 Cash In (Total Cash)',   value: totalCash,        color: 'text-emerald-600' },
              { label: '💳 Card Total',             value: totalCard,        color: 'text-sky-600' },
              { label: '📤 Cash Out (Pay + Salary)', value: cashOut,         color: 'text-red-500' },
              { label: '🏦 Savings',                value: savingsTotal,     color: 'text-violet-600' },
              { label: '💼 Cash Drop',              value: cashDropTotal,    color: 'text-amber-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center justify-between border-b border-stone-100 px-3 py-2 last:border-b-0">
                <span className="text-xs text-stone-600">{label}</span>
                <span className={cx('text-sm font-semibold', color)}>{formatCurrency(value)}</span>
              </div>
            ))}
          </SurfaceCard>

          <SurfaceCard className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Balance Cash = Cash − Out − Savings</div>
            <div className={cx('text-lg font-bold', balanceColor)}>
              {balancePrefix}{formatCurrency(Math.abs(balance))} {balanceIcon}
            </div>
            <div className="mt-1 text-xs text-stone-400">
              {balance > 0 ? 'Extra cash remaining' : balance < 0 ? 'Short — check your figures' : 'Perfectly balanced'}
            </div>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}

function formatHistoryDate(date) {
  try {
    return new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return date;
  }
}

function WastageTab({ entry, setEntry, isStaff, isWastageOnly, isOwner, history, onLoad }) {
  const wastageTotal = entry.wastageReduced?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
  const wastageItemsTotal = entry.wastageItems?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
  const wastageBinTotal = entry.wastageBin?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
  const wastageTakeHomeTotal = entry.wastageTakeHome?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;

  const setField = (field, index, value) => {
    setEntry((cur) => {
      const arr = [...(cur[field] || Array(20).fill(field === 'wastageDesc' ? '' : 0))];
      arr[index] = field === 'wastageDesc' ? value : (parseFloat(value) || 0);
      return { ...cur, [field]: arr };
    });
  };

  return (
    <div className="space-y-3">
      <SectionHeader title="Wastage" />
      <SurfaceCard className="overflow-hidden">
        {/* Header */}
        <div className="border-b-2 border-stone-300 bg-stone-100 px-2 py-1.5" style={{ display: 'grid', gridTemplateColumns: '16px minmax(0,1fr) 46px 46px 36px 32px 36px', gap: '4px', alignItems: 'center' }}>
          <div />
          <div className="text-[9px] font-bold uppercase text-stone-500">Description</div>
          <div className="text-center text-[9px] font-bold uppercase text-stone-500">Orig</div>
          <div className="text-center text-[9px] font-bold uppercase text-red-500">Red</div>
          <div className="text-center text-[9px] font-bold uppercase text-blue-500">Items</div>
          <div className="text-center text-[9px] font-bold uppercase text-orange-500">Bin</div>
          <div className="text-center text-[9px] font-bold uppercase text-purple-500">Home</div>
        </div>

        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className="border-b border-stone-100 px-2 py-1 last:border-b-0" style={{ display: 'grid', gridTemplateColumns: '16px minmax(0,1fr) 46px 46px 36px 32px 36px', gap: '4px', alignItems: 'center' }}>
            <div className="flex h-4 w-4 items-center justify-center rounded-full border border-stone-200 text-[8px] font-bold text-stone-500">
              {i + 1}
            </div>
            <input
              type="text"
              value={entry.wastageDesc?.[i] || ''}
              onChange={(e) => setField('wastageDesc', i, e.target.value)}
              placeholder="Item..."
              className="w-full rounded border border-stone-200 bg-white px-1.5 py-1 text-xs text-stone-900 outline-none focus:border-[#c9a84c]"
            />
            {/* Original Price */}
            <div className="relative">
              <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[9px] text-stone-400">£</span>
              <input type="number" step="0.01" min="0"
                value={entry.wastageOriginal?.[i] || ''}
                onChange={(e) => setField('wastageOriginal', i, e.target.value)}
                placeholder="0"
                className="w-full rounded border border-stone-200 bg-white pl-3 pr-0.5 py-1 text-xs text-stone-700 outline-none focus:border-orange-400"
              />
            </div>
            {/* Reduced Price */}
            <div className="relative">
              <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[9px] text-stone-400">£</span>
              <input type="number" step="0.01" min="0"
                value={entry.wastageReduced?.[i] || ''}
                onChange={(e) => setField('wastageReduced', i, e.target.value)}
                placeholder="0"
                className="w-full rounded border border-stone-200 bg-white pl-3 pr-0.5 py-1 text-xs text-red-600 outline-none focus:border-red-400"
              />
            </div>
            {/* Items qty */}
            <input type="number" min="0"
              value={entry.wastageItems?.[i] || ''}
              onChange={(e) => setField('wastageItems', i, e.target.value)}
              placeholder="0"
              className="w-full rounded border border-stone-200 bg-white px-0.5 py-1 text-center text-xs text-blue-700 outline-none focus:border-blue-400"
            />
            {/* Bin qty */}
            <input type="number" min="0"
              value={entry.wastageBin?.[i] || ''}
              onChange={(e) => setField('wastageBin', i, e.target.value)}
              placeholder="0"
              className="w-full rounded border border-stone-200 bg-white px-0.5 py-1 text-center text-xs text-orange-700 outline-none focus:border-orange-400"
            />
            {/* Take Home qty */}
            <input type="number" min="0"
              value={entry.wastageTakeHome?.[i] || ''}
              onChange={(e) => setField('wastageTakeHome', i, e.target.value)}
              placeholder="0"
              className="w-full rounded border border-stone-200 bg-white px-0.5 py-1 text-center text-xs text-purple-700 outline-none focus:border-purple-400"
            />
          </div>
        ))}

        {/* Totals row */}
        <div className="bg-stone-50 px-2 py-2 border-t-2 border-stone-200" style={{ display: 'grid', gridTemplateColumns: '16px minmax(0,1fr) 46px 46px 36px 32px 36px', gap: '4px', alignItems: 'center' }}>
          <div />
          <div className="text-xs font-bold text-stone-700">Totals</div>
          <div className="text-center text-[11px] font-bold text-stone-600">
            £{(entry.wastageOriginal?.reduce((a, b) => (a||0)+(b||0), 0) || 0).toFixed(2)}
          </div>
          <div className="text-center text-[11px] font-bold text-red-600">
            £{wastageTotal.toFixed(2)}
          </div>
          <div className="text-center text-[11px] font-bold text-blue-600">
            {wastageItemsTotal}
          </div>
          <div className="text-center text-[11px] font-bold text-orange-600">
            {wastageBinTotal}
          </div>
          <div className="text-center text-[11px] font-bold text-purple-600">
            {wastageTakeHomeTotal}
          </div>
        </div>
      </SurfaceCard>

      {/* ── Daily Wastage History — owner + wastage can tap any day to edit ── */}
      {(isWastageOnly || isOwner) && history && history.length > 0 && (() => {
        const weekRecords = history.filter(item => {
          if (!item.date) return false;
          const hasData = (item.wastageDesc?.some(d => d) || item.wastageReduced?.some(v => v > 0) || item.wastageItems?.some(v => v > 0));
          return hasData; // every day that has wastage, tappable to edit
        });
        if (!weekRecords.length) return null;
        return (
          <div className="mt-4">
            <SectionHeader title="Wastage History — Tap to Edit" />
            <div className="space-y-2">
              {weekRecords.map(item => {
                const totalReduced = (item.wastageReduced?.reduce((a,b)=>(a||0)+(b||0),0)||0);
                const totalItems = (item.wastageItems?.reduce((a,b)=>(a||0)+(b||0),0)||0);
                const totalBin = (item.wastageBin?.reduce((a,b)=>(a||0)+(b||0),0)||0);
                const totalTakeHome = (item.wastageTakeHome?.reduce((a,b)=>(a||0)+(b||0),0)||0);
                const entries = item.wastageDesc?.map((d, i) => ({
                  desc: d,
                  original: item.wastageOriginal?.[i] || 0,
                  reduced: item.wastageReduced?.[i] || 0,
                  items: item.wastageItems?.[i] || 0,
                  bin: item.wastageBin?.[i] || 0,
                  takeHome: item.wastageTakeHome?.[i] || 0,
                })).filter(e => e.desc || e.reduced > 0 || e.items > 0) || [];

                return (
                  <SurfaceCard
                    key={item.date}
                    className="overflow-hidden cursor-pointer hover:border-amber-300 border border-transparent"
                    onClick={() => onLoad && onLoad(item.date)}
                  >
                    <div className="flex items-center justify-between bg-stone-50 px-3 py-2 border-b border-stone-200">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-stone-800">{formatHistoryDate(item.date)}</span>
                        <button
                          type="button"
                          onClick={(ev) => { ev.stopPropagation(); onLoad && onLoad(item.date); }}
                          className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700 hover:bg-amber-200"
                        >
                          ✏️ Edit
                        </button>
                      </div>
                      <div className="flex gap-2 text-[10px]">
                        <span className="text-red-600">£{totalReduced.toFixed(2)}</span>
                        <span className="text-blue-600">{totalItems} items</span>
                        <span className="text-orange-600">{totalBin} bin</span>
                        <span className="text-purple-600">{totalTakeHome} home</span>
                      </div>
                    </div>
                    {entries.map((e, i) => (
                      <div key={i} className="flex items-center gap-2 border-b border-stone-100 px-3 py-1.5 last:border-b-0 text-xs">
                        <span className="flex-1 text-stone-700 font-medium">{e.desc || `Item ${i+1}`}</span>
                        {e.original > 0 && <span className="text-stone-400">£{e.original.toFixed(2)}</span>}
                        {e.reduced > 0 && <span className="text-red-500 font-semibold">→ £{e.reduced.toFixed(2)}</span>}
                        {e.items > 0 && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-600">{e.items} items</span>}
                        {e.bin > 0 && <span className="rounded bg-orange-50 px-1.5 py-0.5 text-orange-600">{e.bin} bin</span>}
                        {e.takeHome > 0 && <span className="rounded bg-purple-50 px-1.5 py-0.5 text-purple-600">{e.takeHome} home</span>}
                      </div>
                    ))}
                  </SurfaceCard>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function ScratchTab({ entry, setEntry, history, db, isOwner }) {
  const [slots, setSlots] = useState(
    Array.from({ length: 20 }, () => ({ code: '', price: 0, pack: 60, startNo: 0, endNo: 59 }))
  );
  const [loaded, setLoaded] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  // Load slot setup (code, price, pack, start, end) from Firebase
  useEffect(() => {
    if (!db) { setLoaded(true); return; }
    import('firebase/firestore').then(({ getDoc, doc: fsDoc }) => {
      getDoc(fsDoc(db, 'ao_scratch', 'slots')).then(snap => {
        if (snap.exists() && Array.isArray(snap.data().slots)) {
          const s = snap.data().slots;
          setSlots(Array.from({ length: 20 }, (_, i) => {
            const pack = s[i]?.pack || 60;
            return {
              code: s[i]?.code || '',
              price: s[i]?.price || 0,
              pack,
              startNo: s[i]?.startNo ?? 0,
              endNo: s[i]?.endNo ?? (pack - 1),
            };
          }));
        }
        setLoaded(true);
      }).catch(() => setLoaded(true));
    });
  }, [db]);

  const saveSlots = (newSlots) => {
    setSlots(newSlots);
    if (db) {
      import('firebase/firestore').then(({ setDoc, doc: fsDoc }) => {
        setDoc(fsDoc(db, 'ao_scratch', 'slots'), { slots: newSlots, ts: Date.now() }).catch(() => {});
      });
    }
  };

  const setSlotField = (i, field, val) => {
    const updated = slots.map((s, idx) => {
      if (idx !== i) return s;
      if (field === 'code') return { ...s, code: val };
      if (field === 'pack') {
        const pack = parseInt(val) || 60;
        return { ...s, pack, endNo: pack - 1 }; // auto-set end from pack
      }
      return { ...s, [field]: parseFloat(val) || 0 };
    });
    saveSlots(updated);
  };

  const setFront = (i, val) => {
    const arr = [...(entry.scratchStarts || Array(20).fill(0))];
    arr[i] = parseInt(val) || 0;
    setEntry(cur => ({ ...cur, scratchStarts: arr }));
  };

  // Previous saved day (for sold comparison)
  const prevRecord = (history || [])
    .filter(h => h.date && h.date < entry.date && h.scratchStarts?.some(v => v > 0))
    .sort((a, b) => b.date.localeCompare(a.date))[0];

  const activeSlots = slots
    .map((s, i) => ({ ...s, i }))
    .filter(s => s.price > 0);

  let totalCards = 0, totalValue = 0, totalSold = 0, totalSoldValue = 0;

  const rows = activeSlots.map(s => {
    const front = entry.scratchStarts?.[s.i] || 0;
    const left = front > 0 ? Math.max(0, s.endNo - front) : 0;
    const value = left * s.price;
    totalCards += left;
    totalValue += value;

    // Sold = today front - yesterday front (baseline = setup start no on first day)
    const yestFront = prevRecord?.scratchStarts?.[s.i] || 0;
    const base = yestFront > 0 ? yestFront : s.startNo;
    const sold = (front > 0) ? Math.max(0, front - base) : 0;
    const soldValue = sold * s.price;
    totalSold += sold;
    totalSoldValue += soldValue;

    return { ...s, front, left, value, base, sold, soldValue };
  });

  if (!loaded) {
    return <div className="flex items-center justify-center py-16"><div className="text-sm text-stone-400">Loading...</div></div>;
  }

  return (
    <div className="space-y-3">
      {/* Setup toggle */}
      <button
        onClick={() => setShowSetup(!showSetup)}
        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 flex items-center justify-between"
      >
        <span>⚙️ Set up slots (Code · Price · Start · End · Pack)</span>
        <ChevronDown className={cx('h-4 w-4 transition-transform', showSetup && 'rotate-180')} />
      </button>

      {showSetup && (
        <SurfaceCard className="overflow-hidden">
          <div className="flex items-center gap-1 bg-stone-50 px-2 py-1.5 text-[9px] font-bold uppercase text-stone-500 border-b border-stone-200">
            <span className="w-5">#</span>
            <span className="flex-1">Code</span>
            <span className="w-16 text-center">Price £</span>
            <span className="w-11 text-center">Start</span>
            <span className="w-11 text-center">End</span>
            <span className="w-12 text-center">Pack</span>
          </div>
          {slots.map((s, i) => (
            <div key={i} className="flex items-center gap-1 border-b border-stone-100 px-2 py-1 last:border-b-0">
              <span className="w-5 text-center text-[10px] font-bold text-stone-400">{i + 1}</span>
              <input
                type="text"
                value={s.code}
                onChange={e => setSlotField(i, 'code', e.target.value)}
                placeholder="Code"
                className="flex-1 rounded border border-stone-200 px-2 py-1 text-xs outline-none focus:border-[#c9a84c]"
              />
              <div className="relative w-16">
                <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-stone-400 pointer-events-none">£</span>
                <input
                  type="number"
                  step="0.01"
                  value={s.price || ''}
                  onChange={e => setSlotField(i, 'price', e.target.value)}
                  onBlur={e => { const n = parseFloat(e.target.value); if (!isNaN(n)) setSlotField(i, 'price', n.toFixed(2)); }}
                  placeholder="0.00"
                  className="w-16 rounded border border-stone-200 pl-4 pr-1 py-1 text-right text-xs outline-none focus:border-[#c9a84c]"
                />
              </div>
              <input
                type="number"
                value={s.startNo ?? ''}
                onChange={e => setSlotField(i, 'startNo', e.target.value)}
                placeholder="0"
                className="w-11 rounded border border-stone-200 px-1 py-1 text-center text-xs outline-none focus:border-[#c9a84c]"
              />
              <input
                type="number"
                value={s.endNo ?? ''}
                onChange={e => setSlotField(i, 'endNo', e.target.value)}
                placeholder="59"
                className="w-11 rounded border border-stone-200 px-1 py-1 text-center text-xs outline-none focus:border-[#c9a84c]"
              />
              <input
                type="number"
                value={s.pack || ''}
                onChange={e => setSlotField(i, 'pack', e.target.value)}
                placeholder="60"
                className="w-12 rounded border border-stone-200 px-1 py-1 text-center text-xs outline-none focus:border-[#c9a84c]"
              />
            </div>
          ))}
          <p className="px-3 py-2 text-[10px] text-stone-400">Type any Pack size (60, 80, 120, 180...). End fills in automatically as Pack − 1. You can edit Start/End for a part-used pack.</p>
        </SurfaceCard>
      )}

      {activeSlots.length === 0 && !showSetup && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-4 text-center text-sm text-amber-700">
          No slots set up yet. Tap "Set up slots" above to add your scratch card codes, prices, start/end and pack.
        </div>
      )}

      {activeSlots.length > 0 && (
        <>
          {/* Stock on display */}
          <div>
            <SectionHeader title="End of Day — Value on Display" />
            <SurfaceCard className="overflow-hidden">
              <div className="flex items-center gap-1 bg-stone-50 px-2 py-1.5 text-[9px] font-bold uppercase text-stone-500 border-b border-stone-200">
                <span className="w-5">#</span>
                <span className="w-12">Code</span>
                <span className="w-8 text-right">£</span>
                <span className="flex-1 text-center">Front no.</span>
                <span className="w-10 text-right">End</span>
                <span className="w-10 text-right">Left</span>
                <span className="w-14 text-right">Value</span>
              </div>
              {rows.map(r => (
                <div key={r.i} className="flex items-center gap-1 border-b border-stone-100 px-2 py-1.5 last:border-b-0">
                  <span className="w-5 text-[10px] font-bold text-stone-400">{r.i + 1}</span>
                  <span className="w-12 text-xs text-stone-600">{r.code || '—'}</span>
                  <span className="w-8 text-right text-[11px] text-stone-500">£{r.price}</span>
                  <input
                    type="number"
                    value={r.front || ''}
                    onChange={e => setFront(r.i, e.target.value)}
                    placeholder="0"
                    className="flex-1 mx-1 rounded border border-stone-300 px-2 py-1 text-center text-sm outline-none focus:border-[#c9a84c]"
                  />
                  <span className="w-10 text-right text-xs text-stone-400">{String(r.endNo).padStart(3, '0')}</span>
                  <span className="w-10 text-right text-xs font-medium text-stone-700">{r.left}</span>
                  <span className="w-14 text-right text-xs font-bold text-emerald-600">£{r.value.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex items-center gap-1 bg-stone-50 px-2 py-2">
                <span className="flex-1 text-xs font-bold text-stone-700">Total on Display</span>
                <span className="w-10 text-right text-xs font-bold text-stone-700">{totalCards}</span>
                <span className="w-14 text-right text-sm font-bold text-emerald-600">£{totalValue.toFixed(2)}</span>
              </div>
            </SurfaceCard>
          </div>

          {/* Sold since yesterday */}
          <div>
            <SectionHeader title="Sold Since Yesterday — Check vs Z Report" />
            {!prevRecord ? (
              <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-3 text-center text-xs text-stone-500">
                Day 1 uses each slot's Start no as the baseline. From tomorrow it compares against yesterday's saved numbers.
              </div>
            ) : null}
            <SurfaceCard className="overflow-hidden mt-2">
              <div className="flex items-center gap-1 bg-stone-50 px-2 py-1.5 text-[9px] font-bold uppercase text-stone-500 border-b border-stone-200">
                <span className="w-5">#</span>
                <span className="w-12">Code</span>
                <span className="flex-1 text-right">Was</span>
                <span className="flex-1 text-right">Now</span>
                <span className="w-12 text-right">Sold</span>
                <span className="w-14 text-right">£</span>
              </div>
              {rows.map(r => (
                <div key={r.i} className="flex items-center gap-1 border-b border-stone-100 px-2 py-1.5 last:border-b-0">
                  <span className="w-5 text-[10px] font-bold text-stone-400">{r.i + 1}</span>
                  <span className="w-12 text-xs text-stone-600">{r.code || '—'}</span>
                  <span className="flex-1 text-right text-xs text-stone-400">{r.base || '—'}</span>
                  <span className="flex-1 text-right text-xs text-stone-600">{r.front || '—'}</span>
                  <span className="w-12 text-right text-xs font-medium text-stone-700">{r.sold}</span>
                  <span className="w-14 text-right text-xs font-bold text-blue-600">£{r.soldValue.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex items-center gap-1 bg-blue-50 px-2 py-2">
                <span className="flex-1 text-xs font-bold text-blue-800">Sold Total</span>
                <span className="w-12 text-right text-xs font-bold text-blue-800">{totalSold}</span>
                <span className="w-14 text-right text-sm font-bold text-blue-700">£{totalSoldValue.toFixed(2)}</span>
              </div>
            </SurfaceCard>
            <p className="mt-2 px-1 text-[11px] text-stone-500">
              Check this <b>Sold Total</b> against the SCRATCH line on your Z report. If they match, you're balanced.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

const CC_ITEMS = [
  // 500ML
  'Aloe Vera Drink with Real Aloe Pieces (Original)','Aloe Vera Drink with Real Mango','Appletiser 275ml','Barr Tizer','Bigga Apple','Bigga Fruit Punch','Bigga Orange','Bigga Pineapple','Boost Energy Original','Boost Energy Red Berry','Boost Orange','Boost Original 500ml £1 (Sports Bottle)','Boost Raspberry & Mango','Boost Sport Mixed Berry','Boost Sport Raspberry & Mango','Boost Water Cherry','Boost Water Electrolyte Mango & Passion Fruit','Coca-Cola Zero','Coconut Water Original 1 Litre','Coke 500ml','Diet Coke','Emerge Cherry 500ml','Emerge Orange 500ml','Emerge Sport Caribbean','Emerge Sport Caribbean Style PM 160','Emerge Sport Cherry','Emerge Sport Mango & Passion Fruit','Emerge Sport Tropical Berry','Emerge Strawberry/Watermelon PM 160','Fanta Fruit Twist 500ml','Fanta Green','Fanta Shokata (Blue)','Grace Aloe Original','Lipton Iced Tea Mango','Lipton Tropical Iced Tea','Lipton Tropical Iced Tea 500ml','Lipton Tropical Mango 500ml','Lucozade Apple 500ml','Lucozade Blue Force 500ml','Lucozade Blue Force PM 160','Lucozade Energy Caribbean Crush','Lucozade Orange Sport 500ml','Lucozade Original 500ml','Lucozade Raspberry Sport 500ml','Old Jamaica','Purdey\'s Berry & Dragon 330ml','Purdey\'s Raspberry & Orange 330ml','Rubicon Cherry Burst','Rubicon Sparkling Cherry Burst','Rubicon Very Berry',
  // CANS
  '7UP','7UP Zero Pink Lemonade','Appletiser Can 250ml','Boost Energy Citrus Zero 250ml','Boost Energy Original 250ml','Boost Energy Red Berry Can','Coca-Cola Can 330ml','Coca-Cola Cherry','Coca-Cola Original','Coca-Cola Zero Sugar Can 330ml','Diet Coke Can 330ml','Dr Pepper Cherry','Dr Pepper Cherry Crush Can 330ml','Dr Pepper Zero Sugar Can 330ml','Emerge Sport Caribbean Style 500ml','Emerge Sport Cherry 500ml','Emerge Sport Tropical Berry 500ml','Fanta Chucky Can 330ml','Fanta Fruit Twist Can 330ml','Fanta Orange Can 330ml','Fanta Pineapple & Grapefruit','Grace Aloe Original 500ml','Grace Mighty Malt 99p','Irn-Bru','Ka Black Grape','Ka Fruit Punch','Ka Pineapple 288ml','Ka Sparkling Fruit Punch Can 330ml','Ka Strawberry','Malta Guinness','Mirinda Orange Can 300ml','Mirinda Strawberry Can 330ml','Monster Energy Zero Sugar 500ml','Mountain Dew 500ml','Mountain Dew Citrus Blast','Old Jamaica Ginger Beer','Pepsi 500ml','Pepsi Diet','Pepsi Max Tropical','Purdey\'s Natural Energy 330ml','Red Bull','Red Bull Energy 355ml','Red Bull Energy Drink 473ml','Red Bull Sugar Free 355ml','Red Bull Sugar Free 473ml','Ribena Blackcurrant 250ml','Rio Tropical Pet 500ml','Rubicon Mango','Rubicon Mango Can 70p 330ml','Rubicon Sparkling Lychee Can 330ml','Rubicon Sparkling Mango 330ml','Rubicon Sparkling Mango 500ml £1.29','Rubicon Sparkling Passion','Rubicon Sparkling Tropical 500ml','Sprite','Sprite Can 330ml','Sun Exotic Tropical 288ml','Super Malt Can','Super Malt Original Bottle','Tango Cherry Sugar-Free 500ml','Tizer Can 330ml','Vita Coconut Water 1 Litre','Vita Coconut Water Original 330ml',
  // BIG BOTTLES
  'Aloe Vera Drink Original','Lipton Iced Tea','Lipton Iced Tea Lemon','Lipton Iced Tea Peach','Lucozade Raspberry 750ml',
  // COCONUT WATER
  'Foco Coconut Juice','Perrier Water','Vita Coconut Water 330ml',
  // SMALL CARTONS
  'Just Juice Apple 200ml','Rubicon Still Guava 288ml','Rubicon Still Lychee 288ml','Rubicon Still Mango 288ml','Sun Exotic Pineapple & Coconut 288ml',
  // OTHER
  'Capri Sun Orange 330ml','Mighty Malt Original 330ml',
];

function CashCarryTab({ db }) {
  const [items, setItems] = useState(
    CC_ITEMS.map(name => ({ name, checked: false, qty: '' }))
  );
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [showCheckedOnly, setShowCheckedOnly] = useState(false);
  const [newItem, setNewItem] = useState('');

  // Load from Firebase on mount
  useEffect(() => {
    if (!db) return;
    import('firebase/firestore').then(({ getDoc, doc: firestoreDoc }) => {
      getDoc(firestoreDoc(db, 'ao_cc_list', 'items')).then(snapshot => {
        if (snapshot.exists()) {
          const saved = snapshot.data().items || [];
          // Merge: keep saved state + add any new default items
          const savedNames = new Set(saved.map(i => i.name));
          const newDefaults = CC_ITEMS
            .filter(name => !savedNames.has(name))
            .map(name => ({ name, checked: false, qty: '' }));
          setItems([...saved, ...newDefaults]);
        }
        setLoaded(true);
      }).catch(() => setLoaded(true));
    });
  }, [db]);

  const save = (newItems) => {
    setItems(newItems);
    // Save to Firebase permanently
    if (db) {
      import('firebase/firestore').then(({ setDoc, doc: firestoreDoc }) => {
        setDoc(firestoreDoc(db, 'ao_cc_list', 'items'), {
          items: newItems,
          ts: Date.now()
        }).catch(() => {});
      });
    }
  };

  const toggle = (i) => {
    const updated = items.map((item, idx) => idx === i ? { ...item, checked: !item.checked } : item);
    save(updated);
  };

  const setQty = (i, val) => {
    const updated = items.map((item, idx) => idx === i ? { ...item, qty: val } : item);
    save(updated);
  };

  const clearAll = () => {
    // Only clear ticks and qty — keep all items (including custom ones)
    const updated = items.map(item => ({ ...item, checked: false, qty: '' }));
    save(updated);
  };

  const addCustomItem = () => {
    if (!newItem.trim()) return;
    const updated = [...items, { name: newItem.trim(), checked: false, qty: '', custom: true }];
    save(updated);
    setNewItem('');
  };

  const deleteCustomItem = (i) => {
    const updated = items.filter((_, idx) => idx !== i);
    save(updated);
  };

  // ── Barcode scanning ──
  const [scanning, setScanning] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState(null);
  const [scanName, setScanName] = useState('');
  const [scanMsg, setScanMsg] = useState('');
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ code: '', t: 0 });
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const stopScan = useCallback(async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (e) { /* ignore */ }
    scannerRef.current = null;
    setScanning(false);
  }, []);

  const handleScan = (code) => {
    if (!code) return;
    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.t < 2500) return; // debounce repeats
    lastScanRef.current = { code, t: now };

    const cur = itemsRef.current;
    const idx = cur.findIndex(it => it.barcode === code);
    if (idx >= 0) {
      // Known barcode → tick + qty +1
      const updated = cur.map((it, i) => i === idx
        ? { ...it, checked: true, qty: String((parseInt(it.qty) || 0) + 1) }
        : it);
      save(updated);
      setScanMsg('✓ ' + cur[idx].name);
      return;
    }

    // New barcode → try to look it up online first
    setScanMsg('🔎 Looking up product...');
    fetch(`https://world.openfoodfacts.org/api/v0/product/${code}.json`)
      .then(r => r.json())
      .then(data => {
        const p = data && data.product;
        const name = p && (p.product_name_en || p.product_name || (p.brands ? p.brands : ''));
        if (data && data.status === 1 && name && name.trim()) {
          // Found → add automatically
          const list = itemsRef.current;
          const existing = list.findIndex(it => it.name.toLowerCase() === name.trim().toLowerCase());
          let updated;
          if (existing >= 0) {
            updated = list.map((it, i) => i === existing
              ? { ...it, checked: true, qty: String((parseInt(it.qty) || 0) + 1), barcode: code }
              : it);
          } else {
            updated = [...list, { name: name.trim(), checked: true, qty: '1', custom: true, barcode: code }];
          }
          save(updated);
          setScanMsg('✓ Added: ' + name.trim());
        } else {
          // Not found → ask for name manually
          setPendingBarcode(code);
          setScanName('');
          stopScan();
        }
      })
      .catch(() => {
        // Lookup failed (offline etc.) → ask manually
        setPendingBarcode(code);
        setScanName('');
        stopScan();
      });
  };

  const startScan = () => {
    setScanMsg('');
    setScanning(true);
    setTimeout(async () => {
      if (!window.Html5Qrcode) {
        setScanMsg('Scanner not loaded — check your internet and try again.');
        return;
      }
      try {
        const scanner = new window.Html5Qrcode('cc-reader');
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decoded) => handleScan(decoded),
          () => { /* per-frame failure, ignore */ }
        );
      } catch (e) {
        setScanMsg('Could not open camera. Allow camera access in your browser, then try again.');
      }
    }, 150);
  };

  const saveScannedName = () => {
    const name = scanName.trim();
    if (!name) return;
    const cur = itemsRef.current;
    const existingIdx = cur.findIndex(it => it.name.toLowerCase() === name.toLowerCase());
    let updated;
    if (existingIdx >= 0) {
      updated = cur.map((it, i) => i === existingIdx
        ? { ...it, checked: true, qty: String((parseInt(it.qty) || 0) + 1), barcode: pendingBarcode }
        : it);
    } else {
      updated = [...cur, { name, checked: true, qty: '1', custom: true, barcode: pendingBarcode }];
    }
    save(updated);
    setScanMsg('✓ Saved: ' + name);
    setPendingBarcode(null);
    setScanName('');
  };

  // Clean up camera if component unmounts while scanning
  useEffect(() => () => { if (scannerRef.current) { try { scannerRef.current.stop(); } catch (e) {} } }, []);

  const checkedCount = items.filter(i => i.checked).length;

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-sm text-stone-400">Loading list...</div>
      </div>
    );
  }

  const filtered = items
    .map((item, idx) => ({ ...item, idx }))
    .filter(item => {
      if (showCheckedOnly && !item.checked) return false;
      if (search && !item.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });

  return (
    <div className="space-y-3">
      {/* Header bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search items..."
            className="w-full rounded-lg border border-stone-300 bg-white pl-3 pr-8 py-2 text-sm outline-none focus:border-[#c9a84c]"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-2.5 text-stone-400 text-xs">✕</button>
          )}
        </div>
        <button
          onClick={() => setShowCheckedOnly(!showCheckedOnly)}
          className={cx('rounded-lg px-3 py-2 text-xs font-medium border', showCheckedOnly ? 'bg-[#c9a84c] text-[#183325] border-[#c9a84c]' : 'bg-white border-stone-300 text-stone-600')}
        >
          ✓ {checkedCount}
        </button>
        {checkedCount > 0 && (
          <button onClick={clearAll} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
            Clear
          </button>
        )}
      </div>

      {/* Scan barcode */}
      <button
        onClick={startScan}
        className="w-full rounded-lg bg-[#1f3f2f] px-3 py-2.5 text-sm font-bold text-white flex items-center justify-center gap-2 hover:bg-[#183325]"
      >
        📷 Scan barcode
      </button>

      {/* Scanner overlay */}
      {scanning && (
        <div className="fixed inset-0 z-[80] bg-black/90 flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-sm">
            <div id="cc-reader" className="w-full overflow-hidden rounded-xl bg-black" style={{ minHeight: 240 }} />
            <p className="mt-3 text-center text-sm text-white">{scanMsg || 'Point the camera at a barcode'}</p>
            <button
              onClick={stopScan}
              className="mt-4 w-full rounded-lg bg-white px-4 py-2.5 text-sm font-bold text-stone-800"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* New-barcode name prompt */}
      {pendingBarcode && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4">
          <SurfaceCard className="w-full max-w-sm p-4">
            <div className="text-sm font-bold text-stone-800 mb-1">New barcode</div>
            <div className="text-[11px] text-stone-400 mb-3">{pendingBarcode}</div>
            <p className="text-xs text-stone-600 mb-2">What is this product? (saved for next time)</p>
            <input
              type="text"
              value={scanName}
              autoFocus
              onChange={e => setScanName(e.target.value)}
              onKeyPress={e => { if (e.key === 'Enter') saveScannedName(); }}
              placeholder="e.g. Coca-Cola 500ml"
              className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#c9a84c]"
            />
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => { setPendingBarcode(null); setScanName(''); startScan(); }}
                className="flex-1 rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-600"
              >
                Skip
              </button>
              <button
                onClick={() => { saveScannedName(); startScan(); }}
                disabled={!scanName.trim()}
                className="flex-1 rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-bold text-[#183325] disabled:opacity-40"
              >
                Save & scan next
              </button>
            </div>
          </SurfaceCard>
        </div>
      )}

      {/* Add custom item */}
      <SurfaceCard className="p-3">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            onKeyPress={e => { if (e.key === 'Enter') addCustomItem(); }}
            placeholder="Add missing item..."
            className="flex-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#c9a84c]"
          />
          <button
            onClick={addCustomItem}
            disabled={!newItem.trim()}
            className="rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-bold text-[#183325] disabled:opacity-40"
          >
            + Add
          </button>
        </div>
      </SurfaceCard>

      {/* Summary */}
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center justify-between">
        <span className="text-xs text-emerald-700 font-medium">🛒 Shopping List</span>
        <span className="text-xs text-emerald-700">{checkedCount} of {items.length} selected</span>
      </div>

      {/* Items list */}
      <SurfaceCard className="overflow-hidden">
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-stone-400">No items found</div>
        )}
        {filtered.map(item => (
          <div
            key={item.idx}
            className={cx(
              'flex items-center gap-2 border-b border-stone-100 px-3 py-2 last:border-b-0',
              item.checked ? 'bg-emerald-50' : 'bg-white'
            )}
          >
            {/* Checkbox */}
            <button
              onClick={() => toggle(item.idx)}
              className={cx(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
                item.checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-stone-300 bg-white'
              )}
            >
              {item.checked && <span className="text-[10px] font-bold">✓</span>}
            </button>

            {/* Item name */}
            <span className={cx('flex-1 text-sm', item.checked ? 'line-through text-stone-400' : 'text-stone-800')}>
              {item.name}
            </span>

            {/* Qty input */}
            <input
              type="number"
              min="0"
              value={item.qty}
              onChange={e => setQty(item.idx, e.target.value)}
              placeholder="Qty"
              className="w-14 rounded border border-stone-200 bg-white px-1.5 py-1 text-center text-xs text-stone-700 outline-none focus:border-[#c9a84c]"
            />
            {/* Delete button — only for custom items */}
            {item.custom && (
              <button
                onClick={() => deleteCustomItem(item.idx)}
                className="text-red-400 hover:text-red-600 text-xs px-1"
              >
                🗑
              </button>
            )}
          </div>
        ))}
      </SurfaceCard>
    </div>
  );
}


function buildMonthlyExcel(history, month) {
  const records = history.filter(item => item.date && item.date.startsWith(month));
  if (!records.length) return null;

  const headers = [
    'Date',
    'Total Sales',
    'Shop Sales',
    'Cash In Hand',
    'Card',
    'Daily Payment',
    'Salary',
    'Cash Out',
    // Individual savings breakdown
    'Safe',
    'Green tin',
    'Black tin',
    'Roll',
    'Money bag',
    'Savings Total',
    'Cash Drop',
    'Balance Cash',
  ];

  const rows = records.map(e => {
    const cashOut = (e.dailyPaymentTotal || 0) + (e.salaryTotal || 0);
    const cashIn = e.totalCash || 0;
    const savingsTotal = e.savings?.reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
    const balance = cashIn - cashOut - savingsTotal;
    return [
      e.date,
      (e.totalSales || 0).toFixed(2),
      ((e.t1_shop || 0) + (e.t2_shop || 0)).toFixed(2),
      cashIn.toFixed(2),
      (e.totalCard || 0).toFixed(2),
      (e.dailyPaymentTotal || 0).toFixed(2),
      (e.salaryTotal || 0).toFixed(2),
      cashOut.toFixed(2),
      (e.savings?.[0] || 0).toFixed(2),
      (e.savings?.[1] || 0).toFixed(2),
      (e.savings?.[2] || 0).toFixed(2),
      (e.savings?.[3] || 0).toFixed(2),
      (e.savings?.[4] || 0).toFixed(2),
      savingsTotal.toFixed(2),
      (e.cashDropTotal || 0).toFixed(2),
      balance.toFixed(2),
    ];
  });

  // Monthly totals row at the bottom (Date column shows label)
  const totals = ['MONTHLY TOTAL'];
  for (let col = 1; col < headers.length; col++) {
    const colValues = rows.map(r => parseFloat(r[col]) || 0);
    totals.push(colValues.reduce((a, b) => a + b, 0).toFixed(2));
  }

  const csv = [headers, ...rows, [], totals]
    .map(r => r.map(c => `"${c ?? ''}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Acton_Oriental_${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function buildWastageExcel(history, month) {
  const records = history.filter(item => item.date && item.date.startsWith(month));
  if (!records.length) return;

  const headers = ['Date', 'Item', 'Description', 'Original £', 'Reduced £', 'Items', 'Bin', 'Take Home'];
  const rows = [];

  records.forEach(e => {
    let hasAny = false;
    for (let i = 0; i < 20; i++) {
      const desc = e.wastageDesc?.[i] || '';
      const original = e.wastageOriginal?.[i] || 0;
      const reduced = e.wastageReduced?.[i] || 0;
      const items = e.wastageItems?.[i] || 0;
      const bin = e.wastageBin?.[i] || 0;
      const takeHome = e.wastageTakeHome?.[i] || 0;
      if (desc || original || reduced || items || bin || takeHome) {
        rows.push([e.date, i + 1, desc, original.toFixed(2), reduced.toFixed(2), items, bin, takeHome]);
        hasAny = true;
      }
    }
    if (hasAny) {
      // Totals row per day
      rows.push([
        e.date, 'TOTAL', '',
        (e.wastageOriginal?.reduce((a,b)=>(a||0)+(b||0),0)||0).toFixed(2),
        (e.wastageReduced?.reduce((a,b)=>(a||0)+(b||0),0)||0).toFixed(2),
        e.wastageItems?.reduce((a,b)=>(a||0)+(b||0),0)||0,
        e.wastageBin?.reduce((a,b)=>(a||0)+(b||0),0)||0,
        e.wastageTakeHome?.reduce((a,b)=>(a||0)+(b||0),0)||0,
      ]);
      rows.push([]); // blank row between days
    }
  });

  if (!rows.length) { alert('No wastage data for this month.'); return; }

  const csv = [headers, ...rows].map(r => r.map(c => `"${c ?? ''}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Wastage_${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function HistoryTab({ history, onLoad, onDelete, isStaff }) {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  });

  // Get unique months from history
  const months = [...new Set(history.map(h => h.date?.slice(0,7)).filter(Boolean))].sort().reverse();

  if (!history.length) {
    return (
      <SurfaceCard className="px-6 py-14 text-center">
        <div className="mb-3 text-4xl">📋</div>
        <div className="text-sm font-medium text-stone-900">No records yet.</div>
        <div className="mt-1 text-xs text-stone-500">Fill in the sheet and tap save.</div>
      </SurfaceCard>
    );
  }

  const filteredHistory = selectedMonth === 'all'
    ? history
    : history.filter(h => h.date?.startsWith(selectedMonth));

  return (
    <div className="space-y-3">
      {/* Month selector + Excel export */}
      <div className="flex items-center gap-2">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="flex-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-[#c9a84c]"
        >
          <option value="all">All Records</option>
          {months.map(m => (
            <option key={m} value={m}>
              {new Date(m+'-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </option>
          ))}
        </select>
        {selectedMonth !== 'all' && (
          <div className="flex gap-2">
            <button
              onClick={() => buildMonthlyExcel(history, selectedMonth)}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700"
            >
              <Download className="h-3.5 w-3.5" />
              Sales Excel
            </button>
            <button
              onClick={() => buildWastageExcel(history, selectedMonth)}
              className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-2 text-xs font-bold text-white hover:bg-orange-600"
            >
              <Download className="h-3.5 w-3.5" />
              Wastage Excel
            </button>
          </div>
        )}
      </div>

      {filteredHistory.length === 0 && (
        <SurfaceCard className="px-6 py-8 text-center">
          <div className="text-sm text-stone-500">No records for this month.</div>
        </SurfaceCard>
      )}

      {filteredHistory.map((item) => {
        const cashOut = (item.dailyPaymentTotal || 0) + (item.salaryTotal || 0);
        const cashIn = item.totalCash || 0;
        // Balance = Cash - CashOut - Savings
        const balanceCash = cashIn - cashOut - (item.savingsTotal || 0);
        const balanceColor = balanceCash >= 0 ? '#22c55e' : '#ef4444';
        const shopSales = (item.t1_shop || 0) + (item.t2_shop || 0);
        return (
          <div
            key={`${item.date}-${item.ts ?? 0}`}
            role="button"
            tabIndex={0}
            onClick={() => onLoad(item.date)}
            onKeyDown={(e) => { if (e.key === 'Enter') onLoad(item.date); }}
            className="w-full rounded-xl border border-stone-200 bg-white p-3 text-left shadow-sm transition-all hover:border-amber-300 cursor-pointer"
          >
            <div className="mb-2 flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-bold text-stone-900">{formatHistoryDate(item.date)}</div>
                <div className="text-[10px] uppercase tracking-wider text-stone-400">Tap to edit</div>
                {/* Owner sees who last edited */}
                {!isStaff && item.lastEditedBy && (
                  <div className="mt-0.5 text-[10px] text-stone-400">
                    ✏️ Edited by <span className={cx('font-semibold', item.lastEditedBy === 'staff' ? 'text-blue-500' : 'text-amber-500')}>
                      {item.lastEditedBy === 'staff' ? 'Staff' : 'Owner'}
                    </span>
                    {item.lastEditedAt && <span className="text-stone-400"> · {item.lastEditedAt}</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-base font-bold text-[#c9a84c]">{formatCurrency(item.totalSales)}</div>
                {/* Only owner can delete */}
                {!isStaff && onDelete && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(item.date); }}
                    className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 text-[10px]">
              {isStaff ? (
                // Staff only sees wastage summary
                <>
                  <div className="rounded bg-red-50 px-2 py-1 text-red-600">Reduced <b>£{(item.wastageReduced?.reduce((a,b)=>(a||0)+(b||0),0)||0).toFixed(2)}</b></div>
                  <div className="rounded bg-blue-50 px-2 py-1 text-blue-600">Items <b>{item.wastageItems?.reduce((a,b)=>(a||0)+(b||0),0)||0}</b></div>
                  <div className="rounded bg-orange-50 px-2 py-1 text-orange-600">Bin <b>{item.wastageBin?.reduce((a,b)=>(a||0)+(b||0),0)||0}</b></div>
                  <div className="rounded bg-purple-50 px-2 py-1 text-purple-600">Take Home <b>{item.wastageTakeHome?.reduce((a,b)=>(a||0)+(b||0),0)||0}</b></div>
                </>
              ) : (
                // Owner sees full summary
                <>
                  <div className="rounded bg-stone-100 px-2 py-1 text-stone-600">Total Sales <b>{formatCurrency(item.totalSales)}</b></div>
                  {shopSales > 0 && <div className="rounded bg-stone-100 px-2 py-1 text-stone-600">Shop <b>{formatCurrency(shopSales)}</b></div>}
                  <div className="rounded bg-emerald-50 px-2 py-1 text-emerald-700">Cash <b>{formatCurrency(cashIn)}</b></div>
                  <div className="rounded bg-sky-50 px-2 py-1 text-sky-600">Card <b>{formatCurrency(item.totalCard || 0)}</b></div>
                  <div className="rounded bg-red-50 px-2 py-1 text-red-600">Out <b>{formatCurrency(cashOut)}</b></div>
                  <div className="rounded bg-violet-50 px-2 py-1 text-violet-600">Savings <b>{formatCurrency(item.savingsTotal)}</b></div>
                  <div className="rounded px-2 py-1" style={{ background: balanceCash >= 0 ? '#f0fdf4' : '#fef2f2' }}>
                    Bal <b style={{ color: balanceColor }}>{formatCurrency(balanceCash)}</b>
                  </div>
                  {/* Wastage summary — owner sees this too */}
                  {(item.wastageReduced?.some(v => v > 0) || item.wastageItems?.some(v => v > 0)) && (
                    <>
                      <div className="rounded bg-red-50 px-2 py-1 text-red-600">🗑 Reduced <b>£{(item.wastageReduced?.reduce((a,b)=>(a||0)+(b||0),0)||0).toFixed(2)}</b></div>
                      <div className="rounded bg-blue-50 px-2 py-1 text-blue-600">Items <b>{item.wastageItems?.reduce((a,b)=>(a||0)+(b||0),0)||0}</b></div>
                      <div className="rounded bg-orange-50 px-2 py-1 text-orange-600">Bin <b>{item.wastageBin?.reduce((a,b)=>(a||0)+(b||0),0)||0}</b></div>
                      <div className="rounded bg-purple-50 px-2 py-1 text-purple-600">Home <b>{item.wastageTakeHome?.reduce((a,b)=>(a||0)+(b||0),0)||0}</b></div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
function LoginScreen({ onLogin, onPasswordSubmit }) {
  const [showOwnerPassword, setShowOwnerPassword] = useState(false);
  const [showStaffPin, setShowStaffPin] = useState(false);
  const [ownerPassword, setOwnerPassword] = useState('');
  const [staffPin, setStaffPin] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [staffPinError, setStaffPinError] = useState('');

  const handleOwnerLogin = () => {
    if (ownerPassword === OWNER_PASSWORD) {
      setPasswordError('');
      onPasswordSubmit('owner');
      setOwnerPassword('');
    } else {
      setPasswordError('Incorrect password');
      setOwnerPassword('');
    }
  };

  const handleStaffLogin = () => {
    if (staffPin === STAFF_PIN) {
      setStaffPinError('');
      onLogin('staff');
      setStaffPin('');
    } else if (staffPin === WASTAGE_PIN) {
      setStaffPinError('');
      onLogin('wastage');
      setStaffPin('');
    } else {
      setStaffPinError('Incorrect PIN');
      setStaffPin('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1f3f2f] to-[#183325] flex items-center justify-center p-4">
      <SurfaceCard className="w-full max-w-sm overflow-hidden shadow-2xl">
        <div className="bg-gradient-to-br from-[#1f3f2f] to-[#183325] px-6 py-8 text-center text-white">
          <div className="text-5xl mb-2">🏪</div>
          <div className="text-lg font-bold text-[#c9a84c] mb-1">Acton Oriental</div>
          <div className="text-sm text-white/70">Daily Sales Manager</div>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-center text-sm text-stone-600">Select your role to continue</p>

          {/* Staff PIN */}
          <div className="space-y-2">
            <div className="relative">
              <input
                type={showStaffPin ? 'text' : 'password'}
                value={staffPin}
                onChange={(e) => { setStaffPin(e.target.value); setStaffPinError(''); }}
                onKeyPress={(e) => { if (e.key === 'Enter') handleStaffLogin(); }}
                placeholder="Staff PIN / Wastage PIN"
                maxLength={10}
                className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              />
              <button type="button" onClick={() => setShowStaffPin(!showStaffPin)} className="absolute right-3 top-3 text-stone-500">
                {showStaffPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {staffPinError && <div className="text-xs text-red-500 text-center">{staffPinError}</div>}
            <button
              onClick={handleStaffLogin}
              disabled={!staffPin}
              className="w-full rounded-xl bg-emerald-500 text-white px-4 py-3 font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span>👤</span> Staff / Wastage Login
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-xs text-stone-400">or</span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          {/* Owner Password */}
          <div className="space-y-2">
            <div className="relative">
              <input
                type={showOwnerPassword ? 'text' : 'password'}
                value={ownerPassword}
                onChange={(e) => { setOwnerPassword(e.target.value); setPasswordError(''); }}
                onKeyPress={(e) => { if (e.key === 'Enter') handleOwnerLogin(); }}
                placeholder="Owner password"
                className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 outline-none focus:border-[#c9a84c] focus:ring-2 focus:ring-[#c9a84c]/20"
              />
              <button type="button" onClick={() => setShowOwnerPassword(!showOwnerPassword)} className="absolute right-3 top-3 text-stone-500">
                {showOwnerPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {passwordError && <div className="text-xs text-red-500 text-center">{passwordError}</div>}
            <button
              onClick={handleOwnerLogin}
              disabled={!ownerPassword}
              className="w-full rounded-xl bg-[#c9a84c] text-[#183325] px-4 py-3 font-medium hover:bg-[#d5b867] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span>👑</span> Owner Login
            </button>
          </div>
        </div>
      </SurfaceCard>
    </div>
  );
}

export default function Home() {
  const [role, setRole] = useState(() => {
    if (canUseDOM()) {
      return window.localStorage.getItem(ROLE_STORAGE_KEY) || null;
    }
    return null;
  });

  const { entry, setDate, setEntry, history, refreshHistory, saveEntry, loadEntry, deleteEntry, hasSavedEntryForDate, syncing } = useLocalDataStore();

  const isStaff = role === 'staff';
  const isOwner = role === 'owner';
  const isWastageOnly = role === 'wastage';

  const [activeTab, setActiveTab] = useState('sales');

  useEffect(() => {
    if (isWastageOnly) setActiveTab('wastage');
  }, [isWastageOnly]);

  // Auto-load the saved record for the current date so different roles
  // (sales staff vs wastage) build on the same record instead of overwriting it.
  const loadedDateRef = useRef(null);
  useEffect(() => {
    if (loadedDateRef.current === entry.date) return; // already loaded this date
    const existing = history.find((h) => h.date === entry.date);
    if (existing) {
      loadedDateRef.current = entry.date;
      loadEntry(entry.date);
    }
  }, [history, entry.date, loadEntry]);

  const [moreOpen, setMoreOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [reportSheetOpen, setReportSheetOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const moreRef = useRef(null);
  const reportTextareaRef = useRef(null);

  const saveLabel = useMemo(() => (hasSavedEntryForDate(entry.date) ? 'Update Record' : 'Save Day'), [entry.date, hasSavedEntryForDate]);

  const showToast = useCallback((message) => {
    setToast(message);
    setToastVisible(true);
  }, []);

  const handleLogin = useCallback((selectedRole) => {
    setRole(selectedRole);
    if (canUseDOM()) {
      window.localStorage.setItem(ROLE_STORAGE_KEY, selectedRole);
    }
    showToast(`👋 Welcome, ${selectedRole === 'owner' ? 'Owner' : 'Staff'}!`);
  }, [showToast]);

  const handleLogout = useCallback(() => {
    setRole(null);
    if (canUseDOM()) {
      window.localStorage.removeItem(ROLE_STORAGE_KEY);
    }
    showToast('👋 Logged out');
  }, [showToast]);

  useEffect(() => {
    if (!toastVisible) return undefined;
    const timer = window.setTimeout(() => setToastVisible(false), 2500);
    return () => window.clearTimeout(timer);
  }, [toastVisible, toast]);

  useEffect(() => {
    if (!canUseDOM()) return undefined;
    const onPointerDown = (event) => {
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      refreshHistory();
    }
  }, [activeTab, refreshHistory]);

  useEffect(() => {
    if (!reportSheetOpen) return undefined;
    const timer = window.setTimeout(() => {
      reportTextareaRef.current?.focus();
      reportTextareaRef.current?.select();
      reportTextareaRef.current?.setSelectionRange(0, reportTextareaRef.current.value.length);
    }, 30);
    return () => window.clearTimeout(timer);
  }, [reportSheetOpen, reportText]);

  const handleSave = useCallback(async () => {
    const result = await saveEntry(role);
    if (!result.ok) {
      showToast('⚠️ Set a date first');
      return;
    }
    showToast('✅ Day saved!');
  }, [saveEntry, showToast]);

  const handleExportCsv = useCallback(() => {
    if (!canUseDOM()) return;
    const csv = buildCsvString(entry);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildCsvFilename(entry.date || 'daily-sheet');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setMoreOpen(false);
    showToast('⬇️ CSV downloaded');
  }, [entry, showToast]);

  const handleCopyReport = useCallback(async () => {
    const text = buildClipboardReport(entry);
    try {
      const copied = await copyTextToClipboard(text);
      if (copied) {
        showToast('📋 Copied!');
        setMoreOpen(false);
        setReportSheetOpen(false);
        return;
      }
      setReportText(text);
      setReportSheetOpen(true);
      setMoreOpen(false);
      showToast('📄 Report ready to copy');
    } catch {
      setReportText(text);
      setReportSheetOpen(true);
      setMoreOpen(false);
      showToast('📄 Report ready to copy');
    }
  }, [entry, showToast]);

  const handleClearDay = useCallback(() => {
    setEntry(createEmptyEntry(entry.date));
    setMoreOpen(false);
    showToast('🧹 Day cleared');
  }, [entry.date, setEntry, showToast]);

  const handleLoadHistory = useCallback((date, goToTab = 'sales') => {
    const loaded = loadEntry(date);
    if (!loaded) {
      showToast('⚠️ Record not found');
      return;
    }
    if (goToTab) setActiveTab(goToTab);
    showToast('📂 Record loaded');
  }, [loadEntry, showToast]);

  const handleLoadWastage = useCallback((date) => {
    handleLoadHistory(date, 'wastage');
    if (canUseDOM()) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [handleLoadHistory]);

  const confirmDeleteHistory = useCallback((date) => {
    if (canUseDOM() && !window.confirm(`Delete record for ${date}?`)) {
      return;
    }
    deleteEntry(date);
    if (entry.date === date) {
      setEntry(createEmptyEntry(date));
    }
    showToast('🗑️ Record deleted');
  }, [deleteEntry, entry.date, setEntry, showToast]);

  if (!role) {
    return <LoginScreen onLogin={handleLogin} onPasswordSubmit={handleLogin} />;
  }

  const visibleTabs = isWastageOnly
    ? tabs.filter(t => t.key === 'wastage' || t.key === 'cashcarry')
    : isStaff
    ? tabs.filter(t => t.key === 'sales' || t.key === 'zreport' || t.key === 'balance' || t.key === 'scratch')
    : tabs;

  return (
    <div className="min-h-screen bg-stone-100 pb-28 text-stone-900">
      <header className="sticky top-0 z-40 border-b border-[#c9a84c]/30 bg-gradient-to-br from-[#1f3f2f] to-[#183325] text-white shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="mx-auto max-w-md px-3 pb-2 pt-2">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <div className="text-base font-extrabold text-[#c9a84c] leading-tight">Acton Oriental</div>
              <div className="text-[9px] uppercase tracking-widest text-white/60">{isOwner ? '👑 Owner Mode' : isWastageOnly ? '🗑️ Wastage Mode' : '👤 Staff Mode'} • Daily Sales Sheet</div>
            </div>
            <div className="flex gap-1.5">
              <div className="rounded-md bg-[#c9a84c] px-2 py-1 shadow-sm">
                <input type="date" value={entry.date} onChange={(e) => setDate(e.target.value)} className="bg-transparent text-[10px] font-medium text-[#183325] outline-none" />
              </div>
              <button
                onClick={handleLogout}
                className="rounded-md bg-red-500/20 hover:bg-red-500/30 p-1 text-red-400 transition-colors"
                title="Logout"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className={cx("grid gap-1 rounded-lg bg-black/20 p-0.5", isWastageOnly ? 'grid-cols-2' : isStaff ? 'grid-cols-4' : 'grid-cols-4')}>
            {visibleTabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.key);
                    setMoreOpen(false);
                  }}
                  className={cx('rounded-md px-1 py-1.5 text-[10px] font-medium uppercase tracking-wider transition-all', isActive ? 'bg-[#c9a84c] text-[#183325]' : 'text-white/60')}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-md px-3 pt-2">
        {activeTab === 'sales' && !isWastageOnly && <SalesTab entry={entry} setEntry={setEntry} isStaff={isStaff} />}
        {activeTab === 'zreport' && !isWastageOnly && <ZReportTab entry={entry} setEntry={setEntry} isStaff={isStaff} />}
        {activeTab === 'balance' && !isWastageOnly && <BalanceTab entry={entry} setEntry={setEntry} isStaff={isStaff} />}
        {activeTab === 'wastage' && <WastageTab entry={entry} setEntry={setEntry} isStaff={isStaff} isWastageOnly={isWastageOnly} isOwner={isOwner} history={history} onLoad={handleLoadWastage} />}
        {activeTab === 'scratch' && (isStaff || isOwner) && <ScratchTab entry={entry} setEntry={setEntry} history={history} db={db} isOwner={isOwner} />}
        {activeTab === 'cashcarry' && (isWastageOnly || isOwner) && <CashCarryTab db={db} />}
        {isOwner && activeTab === 'history' && <HistoryTab history={history} onLoad={handleLoadHistory} onDelete={confirmDeleteHistory} isStaff={isStaff} />}
      </main>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-stone-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="mx-auto flex max-w-md items-center gap-2 px-3 py-2">
          {isOwner && (
            <div className="relative flex-1" ref={moreRef}>
              <AppButton type="button" variant="outline" onClick={() => setMoreOpen((current) => !current)} className="w-full justify-between">
                <span className="flex items-center gap-2"><HistoryIcon className="h-4 w-4" />More</span>
                <ChevronDown className={cx('h-4 w-4 transition-transform', moreOpen && 'rotate-180')} />
              </AppButton>
              {moreOpen && (
                <SurfaceCard className="absolute bottom-[calc(100%+10px)] left-0 right-0 overflow-hidden shadow-xl">
                  <button type="button" onClick={handleExportCsv} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-stone-800 hover:bg-stone-100"><Download className="h-4 w-4 text-[#c9a84c]" />Export CSV</button>
                  <button type="button" onClick={handleCopyReport} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-stone-800 hover:bg-stone-100"><Copy className="h-4 w-4 text-[#c9a84c]" />Copy Report</button>
                  <button type="button" onClick={handleClearDay} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-500 hover:bg-stone-100"><X className="h-4 w-4" />Clear Day</button>
                </SurfaceCard>
              )}
            </div>
          )}
          <AppButton type="button" onClick={handleSave} disabled={syncing} className={isOwner ? "flex-[1.15]" : "flex-1"}>
            <Save className="mr-2 h-4 w-4" />{syncing ? 'Saving...' : saveLabel}
          </AppButton>
        </div>
      </div>

      {reportSheetOpen && isOwner && (
        <div className="fixed inset-0 z-[70] bg-black/50 p-4 backdrop-blur-sm">
          <SurfaceCard className="mx-auto mt-10 max-w-md overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
              <div>
                <div className="text-sm font-bold text-stone-900">Copy Report</div>
                <div className="text-[11px] text-stone-500">Clipboard is blocked here, so the report is ready below.</div>
              </div>
              <button type="button" onClick={() => setReportSheetOpen(false)} className="rounded-lg p-2 text-stone-500 hover:bg-stone-100 hover:text-stone-900" aria-label="Close report sheet">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 p-4">
              <textarea ref={reportTextareaRef} value={reportText} readOnly className="h-72 w-full rounded-xl border border-stone-300 bg-stone-50 px-3 py-3 text-xs text-stone-900 outline-none font-mono" />
              <div className="grid grid-cols-2 gap-3">
                <AppButton type="button" variant="outline" onClick={() => {
                  reportTextareaRef.current?.focus();
                  reportTextareaRef.current?.select();
                  reportTextareaRef.current?.setSelectionRange(0, reportTextareaRef.current.value.length);
                  showToast('✂️ Text selected');
                }}>Select All</AppButton>
                <AppButton type="button" onClick={() => setReportSheetOpen(false)}>Done</AppButton>
              </div>
            </div>
          </SurfaceCard>
        </div>
      )}

      <div className={cx('pointer-events-none fixed bottom-24 left-1/2 z-[60] -translate-x-1/2 rounded-full bg-[#c9a84c] px-5 py-2 text-sm font-bold text-[#183325] shadow-lg transition-all duration-300', toastVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0')}>
        {toast}
      </div>
    </div>
  );
}
